import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDateLocale } from "../../../hooks/useDateLocale";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { AuthContext } from "../../../contexts/AuthContext";
import {
  loyaltyService,
  LOYALTY_PLANS,
  LoyaltyPlan,
} from "../../../services/loyaltyService";

import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

type Props = {
  onClose: () => void;
  openSucessAlert?: (message?: string) => void;
  openErrorAlert?: (message?: string) => void;
};

type StatusFilter = "ALL" | "ACTIVE" | "INACTIVE";

interface FormValues {
  startDate: string;
  endDate: string;
  loyaltyPlan: LoyaltyPlan | "ALL";
  status: StatusFilter;
}

interface ReportJob {
  jobId: string;
  downloadUrl: string;
  status: "pending" | "processing" | "done" | "failed";
}

export default function ModalLoyaltyReport({
  onClose,
  openSucessAlert,
  openErrorAlert,
}: Props) {
  const { t } = useTranslation();
  const { dateFormat, adapterLocale } = useDateLocale();
  const { tokenHeaders } = useContext(AuthContext);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reportJob, setReportJob] = useState<ReportJob>({
    jobId: "",
    downloadUrl: "",
    status: "pending",
  });

  const schema = yup.object().shape({
    startDate: yup
      .string()
      .required(t("loyalty.report.validation.startDateRequired")),
    endDate: yup
      .string()
      .required(t("loyalty.report.validation.endDateRequired"))
      .test(
        "is-after-start",
        t("loyalty.report.validation.endDateGreaterThanStartDate"),
        (value, ctx) => {
          const { startDate } = ctx.parent as FormValues;
          if (!startDate || !value) return true;
          return value > startDate;
        },
      ),
    loyaltyPlan: yup
      .string()
      .required(t("loyalty.report.validation.categoryRequired")),
    status: yup
      .string()
      .required(t("loyalty.report.validation.statusRequired")),
  });

  const { register, handleSubmit, formState, watch, control } =
    useForm<FormValues>({
      resolver: yupResolver(schema) as unknown as Resolver<FormValues>,
      mode: "onChange",
      reValidateMode: "onChange",
      defaultValues: {
        startDate: "",
        endDate: "",
        loyaltyPlan: "" as LoyaltyPlan,
        status: "" as StatusFilter,
      },
    });

  const { errors } = formState;
  const selectedPlan = watch("loyaltyPlan");
  const selectedStatus = watch("status");

  const triggerDownload = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.click();
  };

  const pollReportStatus = async (jobId: string) => {
    try {
      const result = await loyaltyService.getReportJob(jobId, tokenHeaders);

      if (result.data.status === "PROCESSING") {
        pollingRef.current = setTimeout(() => pollReportStatus(jobId), 5000);
        return;
      }

      if (result.data.status === "DONE") {
        setReportJob({
          jobId,
          downloadUrl: result.data.downloadUrl ?? "",
          status: "done",
        });
        return;
      }

      setReportJob({ jobId, downloadUrl: "", status: "failed" });
    } catch {
      setReportJob((prev) => ({ ...prev, status: "failed" }));
    }
  };

  useEffect(() => {
    if (reportJob.status === "processing" && reportJob.jobId) {
      pollReportStatus(reportJob.jobId);
      return;
    }

    if (reportJob.status === "done" && reportJob.downloadUrl) {
      triggerDownload(reportJob.downloadUrl);
      openSucessAlert?.(t("loyalty.report.successMessage"));
      onClose();
      return;
    }

    if (reportJob.status === "failed") {
      openErrorAlert?.();
    }
  }, [reportJob.status]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const generateReport: SubmitHandler<FormValues> = async (values) => {
    try {
      const params: Parameters<typeof loyaltyService.requestReport>[0] = {
        startDate: values.startDate,
        endDate: values.endDate,
      };

      if (values.loyaltyPlan !== "ALL") {
        params.loyaltyPlan = values.loyaltyPlan as LoyaltyPlan;
      }

      if (values.status !== "ALL") {
        params.active = values.status === "ACTIVE";
      }

      const response = await loyaltyService.requestReport(params, tokenHeaders);

      setReportJob({
        jobId: response.data.jobId,
        downloadUrl: "",
        status: "processing",
      });
    } catch (error: any) {
      openErrorAlert?.(error?.response?.data?.message);
    }
  };

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const isProcessing =
    reportJob.status === "processing" || formState.isSubmitting;

  return (
    <MyModal
      title={t("loyalty.report.title")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(generateReport)}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 400,
          marginBottom: 3,
          marginTop: 1,
        }}
      >
        {t("loyalty.report.description")}
      </Typography>

      {HorizontalLine}

      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={adapterLocale}
      >
        <Typography sx={{ fontWeight: "bold", marginBottom: 1.5 }}>
          {t("loyalty.report.period")}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t("loyalty.report.startDate")}
                  format={dateFormat}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(val) =>
                    field.onChange(val ? dayjs(val).format("YYYY-MM-DD") : "")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.startDate,
                      helperText: errors.startDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t("loyalty.report.endDate")}
                  format={dateFormat}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(val) =>
                    field.onChange(val ? dayjs(val).format("YYYY-MM-DD") : "")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.endDate,
                      helperText: errors.endDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      {HorizontalLine}

      <Typography sx={{ fontWeight: "bold", marginBottom: 1.5 }}>
        {t("loyalty.report.parameters")}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormInputText
            select
            id="loyaltyPlan"
            label={t("loyalty.report.category")}
            error={errors.loyaltyPlan}
            value={selectedPlan || ""}
            {...register("loyaltyPlan")}
          >
            <MenuItem value="ALL">{t("loyalty.plans.ALL")}</MenuItem>
            {LOYALTY_PLANS.map((plan) => (
              <MenuItem key={plan} value={plan}>
                {t(`loyalty.plans.${plan}`)}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormInputText
            select
            id="status"
            label={t("loyalty.report.status")}
            error={errors.status}
            value={selectedStatus || ""}
            {...register("status")}
          >
            <MenuItem value="ALL">{t("loyalty.status.all")}</MenuItem>
            <MenuItem value="ACTIVE">{t("loyalty.status.active")}</MenuItem>
            <MenuItem value="INACTIVE">{t("loyalty.status.inactive")}</MenuItem>
          </FormInputText>
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("loyalty.report.requiredFields")}
      </p>

      {isProcessing ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CircularProgress />
        </Box>
      ) : (
        <ActionButtons
          typeButton="submit"
          saveName={t("loyalty.report.downloadButton")}
          disableSave={!formState.isValid}
          enableLine={false}
          sx={{ justifyContent: "flex-end" }}
        />
      )}
    </MyModal>
  );
}
